import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { submitAnswer, pauseTimer, resumeTimer } from "../store/sessionSlice";
import { addTranscriptEntry, setFinalScore } from "../store/candidateSlice";
import Timer from "./Timer";
import { callOpenAI } from "../api/openai";
import {
  Card,
  Input,
  Button,
  message,
  Typography,
  Avatar,
  Progress,
  Row,
  Col,
  Tag,
} from "antd";
import {
  UserOutlined,
  RobotOutlined,
  SendOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Text, Title } = Typography;

export default function InterviewChat() {
  const { candidateId } = useParams();
  const dispatch = useDispatch();
  const session = useSelector((s) => s.session);
  const pauseflag = useSelector((s) => s.session.pauseflag);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const curIndex = session.currentQuestionIndex;
  const curQ = session.questions[curIndex];
  const remaining = curQ ? session.timers[curQ.id] : 0;
  const totalQuestions = session.questions.length;
  const progressPercentage = (curIndex / totalQuestions) * 100;


  const handlePause = () => {
    if (pauseflag) {
      dispatch(resumeTimer());
      
    } else {
      dispatch(pauseTimer());
    }
  };

 
  useEffect(() => {
    const forceSave = () => {
      if (window.__PERSISTOR) window.__PERSISTOR.flush();
    };
    const handleBeforeUnload = () => forceSave();

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      forceSave();
    };
  }, []);

  useEffect(() => {
    if (session.answers.length > 0 && window.__PERSISTOR) {
      setTimeout(() => window.__PERSISTOR.flush(), 100);
    }
  }, [session.answers.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session.answers, curIndex]);

  useEffect(() => {
    const inputElement = document.querySelector("textarea");
    if (inputElement) inputElement.focus();
  }, [curIndex]);

  const chatMessages = [];

  session.questions.slice(0, curIndex).forEach((question, index) => {
    const answer = session.answers.find((ans) => ans.questionId === question.id);
    chatMessages.push({
      type: "question",
      content: question.text,
      timestamp: index,
      score: answer?.score,
    });
    if (answer) {
      chatMessages.push({
        type: "answer",
        content: answer.answer,
        timestamp: index + 0.5,
        score: answer.score,
        feedback: answer.feedback,
      });
    }
  });

  if (curQ) {
    chatMessages.push({
      type: "question",
      content: curQ.text,
      timestamp: curIndex,
      isCurrent: true,
    });
  }

  async function handleSubmit(isAutoSubmit = false) {
    if (!curQ) return;

    setLoading(true);
    let ans = input.trim();
    if (isAutoSubmit && !ans) ans = "[Time's Up - Auto Submitted]";

    if (ans && ans !== "[Time's Up - Auto Submitted]") {
      const messages = [
        {
          role: "system",
          content: `You are an interview scoring assistant. Analyze the answer and provide a score from 0-10 with constructive feedback.`,
        },
        {
          role: "user",
          content: `Question: ${curQ.text}\nAnswer: ${ans}\nReturn JSON format: { "score": number, "feedback": "string" }`,
        },
      ];

      try {
        const resp = await callOpenAI(messages);
        const parsed = JSON.parse(resp.choices[0].message.content);

        dispatch(
          submitAnswer({
            questionId: curQ.id,
            answer: ans,
            score: parsed.score,
            feedback: parsed.feedback,
          })
        );

        dispatch(
          addTranscriptEntry({
            id: candidateId,
            entry: {
              question: curQ.text,
              answer: ans,
              score: parsed.score,
              feedback: parsed.feedback,
            },
          })
        );
      } catch (error) {
        console.error("AI scoring failed:", error);
        message.error("AI scoring failed");

        dispatch(
          submitAnswer({
            questionId: curQ.id,
            answer: ans,
            score: isAutoSubmit ? 0 : 5,
            feedback: "AI evaluation failed - manual review needed",
          })
        );
      }
    } else {
      const feedback = isAutoSubmit
        ? "Time's up - no answer provided"
        : "Empty answer submitted";

      dispatch(
        submitAnswer({
          questionId: curQ.id,
          answer: ans,
          score: 0,
          feedback,
        })
      );
    }

    setTimeout(() => {
      if (window.__PERSISTOR) window.__PERSISTOR.flush();
    }, 200);

    if (curIndex + 1 === session.questions.length) {
      const totalScore = session.answers.reduce(
        (sum, a) => sum + (a.score || 0),
        0
      );
      const avg = session.answers.length
        ? totalScore / session.answers.length
        : 0;
      dispatch(
        setFinalScore({
          id: candidateId,
          score: Math.round(avg * 10) / 10,
          summary: `Final assessment: Average score ${Math.round(avg * 10) / 10}/10`,
        })
      );
    }

    setInput("");
    setLoading(false);
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "#52c41a";
    if (score >= 6) return "#faad14";
    return "#f5222d";
  };

  if (!curQ) {
    return (
      <div
        style={{
          height: "100vh",
          background: "linear-gradient(135deg, #001529 0%, #1890ff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Card
          style={{
            width: "500px",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            background: "rgba(255, 255, 255, 0.95)",
          }}
          bodyStyle={{ padding: "40px", textAlign: "center" }}
        >
          <Text style={{ fontSize: "32px" }}>🎉</Text>
          <Title level={2}>Interview Completed!</Title>
          <Text>Your results have been saved.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100vh",
        background: "linear-gradient(135deg, #001529 0%, #1890ff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Card
        style={{
          width: "95%",
          maxWidth: "1000px",
          height: "90vh",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          background: "rgba(255, 255, 255, 0.95)",
        }}
        bodyStyle={{
          padding: "0",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #f0f0f0",
            background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ color: "white", margin: 0 }}>
                AI Interview Session
              </Title>
              <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                Question {curIndex + 1} of {totalQuestions}
              </Text>
            </Col>
            <Col>
              <Tag color="blue">
                <ClockCircleOutlined /> Time: {curQ.timeLimit}s
              </Tag>
            </Col>
          </Row>
        </div>

        {/* Chat Section */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            background: "#fafafa",
          }}
        >
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            {chatMessages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  marginBottom: "20px",
                  justifyContent:
                    message.type === "question" ? "flex-start" : "flex-end",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection:
                      message.type === "question" ? "row" : "row-reverse",
                    alignItems: "flex-start",
                    maxWidth: "70%",
                    gap: "12px",
                  }}
                >
                  <Avatar
                    size="large"
                    icon={
                      message.type === "question" ? (
                        <RobotOutlined />
                      ) : (
                        <UserOutlined />
                      )
                    }
                    style={{
                      backgroundColor:
                        message.type === "question" ? "#1890ff" : "#52c41a",
                    }}
                  />
                  <div
                    style={{
                      background:
                        message.type === "question"
                          ? "white"
                          : "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                      padding: "16px 20px",
                      borderRadius: "18px",
                      border: message.isCurrent
                        ? "2px solid #1890ff"
                        : "1px solid #e8e8e8",
                    }}
                  >
                    <Text
                      style={{
                        color:
                          message.type === "question" ? "#001529" : "white",
                      }}
                    >
                      {message.content}
                    </Text>
                    {message.score !== undefined && (
                      <div style={{ marginTop: "12px" }}>
                        <Tag
                          color={getScoreColor(message.score)}
                          style={{
                            border: "none",
                            color: "white",
                            fontWeight: "600",
                          }}
                        >
                          Score: {message.score}/10
                        </Tag>
                        {message.feedback && (
                          <Text
                            style={{
                              fontSize: "12px",
                              color:
                                message.type === "question"
                                  ? "#666"
                                  : "rgba(255,255,255,0.8)",
                              display: "block",
                              marginTop: "8px",
                            }}
                          >
                            {message.feedback}
                          </Text>
                        )}
                      </div>
                    )}
                    {message.isCurrent && (
                      <div style={{ marginTop: "16px" }}>
                        <Timer
                          questionId={curQ.id}
                          remaining={remaining}
                          total={curQ.timeLimit}
                          onTimeUp={() => handleSubmit(true)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: "24px",
            borderTop: "1px solid #f0f0f0",
            background: "white",
          }}
        >
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <TextArea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer here... (Press Enter to send)"
              disabled={loading || pauseflag}
              style={{
                marginBottom: "12px",
                borderRadius: "12px",
                border: "2px solid #e8e8e8",
                padding: "16px",
                fontSize: "14px",
                resize: "none",
                backgroundColor: pauseflag ? "#f5f5f5" : "white",
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => handleSubmit(false)}
              loading={loading}
              disabled={!input.trim() || pauseflag}
              style={{
                width: "100%",
                height: "50px",
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                border: "none",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Submit Answer {remaining > 0 && `(${remaining}s remaining)`}
            </Button>

            <Button
              onClick={handlePause}
              
              
            >
              {pauseflag ? "Resume" : "Pause"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
